import { useEffect, useState } from "react";
import { Search, Images } from "lucide-react";
import { api } from "@/lib/api";
import { PeepingLogo } from "@/components/PeepingLogo";
import { MediaImage, LockedOverlay } from "@/components/Media";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const ACCESS_LABEL = { public: "Preview", subscriber: "Subscriber", member: "Members Only", creator: "Creator" };

export default function Photos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [album, setAlbum] = useState(null);

  useEffect(() => {
    api
      .get("/content/photos")
      .then((r) => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="photos-screen" className="px-5 pt-6">
      <header className="flex items-center justify-between">
        <PeepingLogo className="w-28 items-start" showTagline={false} />
        <Search size={20} className="text-white/80" />
      </header>

      <h1 className="mt-4 text-2xl font-extrabold">Photos</h1>

      <section className="mt-5">
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="aspect-square animate-pulse rounded-2xl bg-white/5" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="photos-empty" className="rounded-2xl glass p-8 text-center">
            <Images size={26} className="mx-auto text-[#6f6878]" />
            <p className="mt-2 text-sm font-semibold">New photo collections are coming soon.</p>
            <p className="mt-1 text-xs text-[#a7a0af]">Creators' photo albums will show up here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map((p) => (
              <button
                key={p.id}
                data-testid={`photo-set-${p.id}`}
                onClick={() => !p.locked && setAlbum(p)}
                className="relative aspect-square overflow-hidden rounded-2xl text-left"
              >
                <MediaImage url={p.cover_url} alt={p.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                {p.locked && <LockedOverlay label={ACCESS_LABEL[p.access_level]} />}
                <div className="absolute bottom-2 left-2 right-2">
                  <p className="truncate text-sm font-semibold">{p.title}</p>
                  <p className="text-[0.65rem] text-[#cfc8d8]">{p.photo_count} Photos</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!album} onOpenChange={(o) => !o && setAlbum(null)}>
        <DialogContent className="max-w-lg border-white/10 bg-[#0c0a10] text-white">
          <DialogTitle>{album?.title}</DialogTitle>
          <div className="grid max-h-[60vh] grid-cols-2 gap-2 overflow-y-auto no-scrollbar">
            {(album?.photo_urls || []).map((u, i) => (
              <MediaImage key={i} url={u} className="aspect-square w-full object-cover" rounded="rounded-xl" />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
